import React, { useState } from 'react';
import { useAnnouncements, Announcement } from '@/shared/hooks/useAnnouncements';
import { useAuth } from '@/shared/hooks/useAuth';
import { Bell, Plus, Edit2, Trash2, Eye, EyeOff, AlertCircle, CheckCircle2, X, Megaphone } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

const AdminAnnouncementsPage: React.FC = () => {
    const { announcements, loading, createAnnouncement, updateAnnouncement, deleteAnnouncement } = useAnnouncements(false);
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: '공지' as Announcement['category'],
        priority: 'normal' as Announcement['priority'],
        isActive: true
    });

    const resetForm = () => {
        setFormData({
            title: '',
            content: '',
            category: '공지',
            priority: 'normal',
            isActive: true
        });
        setEditingAnnouncement(null);
    };

    const openCreateModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const openEditModal = (announcement: Announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            title: announcement.title,
            content: announcement.content,
            category: announcement.category,
            priority: announcement.priority,
            isActive: announcement.isActive
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim() || !formData.content.trim() || !user) return;

        if (editingAnnouncement) {
            await updateAnnouncement(editingAnnouncement.id, formData);
        } else {
            await createAnnouncement({
                ...formData,
                author: user.displayName || user.email || 'Admin'
            });
        }

        setIsModalOpen(false);
        resetForm();
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('정말 이 공지사항을 삭제하시겠습니까?')) {
            await deleteAnnouncement(id);
        }
    };

    const toggleActive = async (announcement: Announcement) => {
        await updateAnnouncement(announcement.id, { isActive: !announcement.isActive });
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case '공지': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case '점검': return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
            case '상장': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
            case '이벤트': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
            case '보안': return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
            default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-muted-foreground">로딩 중...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-8 space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-2">
                    <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                        <Megaphone className="h-8 w-8 text-blue-500" />
                        공지사항 관리
                    </h1>
                    <p className="text-sm text-muted-foreground">공지사항을 생성, 수정, 삭제할 수 있습니다.</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-xs uppercase flex items-center gap-2 transition-all"
                >
                    <Plus className="h-4 w-4" />
                    새 공지사항
                </button>
            </div>

            {/* Announcements List */}
            <div className="space-y-4">
                {announcements.length === 0 ? (
                    <div className="p-12 text-center border border-dashed border-border rounded-2xl">
                        <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground font-bold">등록된 공지사항이 없습니다.</p>
                    </div>
                ) : (
                    announcements.map(announcement => (
                        <div key={announcement.id} className={cn(
                            "p-6 rounded-2xl border transition-all",
                            announcement.isActive ? "bg-card border-border" : "bg-accent/30 border-dashed border-border opacity-60"
                        )}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 space-y-3">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className={cn("px-3 py-1 rounded-full text-xs font-black uppercase border", getCategoryColor(announcement.category))}>
                                            {announcement.category}
                                        </span>
                                        {announcement.priority === 'high' && (
                                            <span className="px-3 py-1 rounded-full text-xs font-black bg-rose-500/10 text-rose-500 border border-rose-500/20 uppercase">
                                                긴급
                                            </span>
                                        )}
                                        {!announcement.isActive && (
                                            <span className="px-3 py-1 rounded-full text-xs font-black bg-gray-500/10 text-gray-500 border border-gray-500/20 uppercase">
                                                비활성
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-xl font-black">{announcement.title}</h3>
                                    <p className="text-sm text-muted-foreground line-clamp-2">{announcement.content}</p>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground font-bold">
                                        <span>작성자: {announcement.author}</span>
                                        <span>•</span>
                                        <span>{announcement.createdAt?.toDate().toLocaleString('ko-KR')}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => toggleActive(announcement)}
                                        className={cn(
                                            "h-10 w-10 rounded-xl border flex items-center justify-center transition-all",
                                            announcement.isActive
                                                ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500 hover:bg-emerald-500/20"
                                                : "bg-gray-500/10 border-gray-500/20 text-gray-500 hover:bg-gray-500/20"
                                        )}
                                        title={announcement.isActive ? "비활성화" : "활성화"}
                                    >
                                        {announcement.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                    </button>
                                    <button
                                        onClick={() => openEditModal(announcement)}
                                        className="h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-500 hover:bg-blue-500/20 flex items-center justify-center transition-all"
                                        title="수정"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(announcement.id)}
                                        className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 hover:bg-rose-500/20 flex items-center justify-center transition-all"
                                        title="삭제"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-card border border-border rounded-3xl w-full max-w-2xl p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-black uppercase">
                                {editingAnnouncement ? '공지사항 수정' : '새 공지사항'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="h-10 w-10 rounded-xl hover:bg-accent flex items-center justify-center">
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-black mb-2">제목</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full h-12 px-4 rounded-xl bg-accent border border-border outline-none focus:border-blue-500 text-sm font-bold"
                                    placeholder="공지사항 제목을 입력하세요"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-black mb-2">내용</label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    rows={8}
                                    className="w-full px-4 py-3 rounded-xl bg-accent border border-border outline-none focus:border-blue-500 text-sm font-bold resize-none"
                                    placeholder="공지사항 내용을 입력하세요"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-black mb-2">카테고리</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value as Announcement['category'] })}
                                        className="w-full h-12 px-4 rounded-xl bg-accent border border-border outline-none focus:border-blue-500 text-sm font-bold"
                                    >
                                        <option value="공지">공지</option>
                                        <option value="점검">점검</option>
                                        <option value="상장">상장</option>
                                        <option value="이벤트">이벤트</option>
                                        <option value="보안">보안</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-black mb-2">우선순위</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({ ...formData, priority: e.target.value as Announcement['priority'] })}
                                        className="w-full h-12 px-4 rounded-xl bg-accent border border-border outline-none focus:border-blue-500 text-sm font-bold"
                                    >
                                        <option value="low">낮음</option>
                                        <option value="normal">보통</option>
                                        <option value="high">높음 (긴급)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    className="h-5 w-5 rounded"
                                />
                                <label htmlFor="isActive" className="text-sm font-bold cursor-pointer">
                                    즉시 게시 (활성화)
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 h-12 rounded-xl bg-accent border border-border text-sm font-black uppercase hover:bg-accent/80 transition-all"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-black uppercase transition-all flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                {editingAnnouncement ? '수정' : '생성'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAnnouncementsPage;
